/**
 * 保函申请草稿（Redis 缓存）前端工具
 *
 * 保函类型 orderType 对应：
 *   BANK_8000          银行保函（8000）
 *   ELECTRONIC_9600    电子担保保函（9600）
 *   INSURANCE_7000     保险保函（7000）
 *   PERFORMANCE_9100   履约保函（9100）
 *
 * 缓存层次：
 *   1. 同流程内 step 切换 → Vuex 内存态（单页应用不刷新，组件不销毁，数据自然存活）
 *   2. 跨流程/跨设备恢复 → Redis 草稿（7天有效期，点保存才存，显式弹窗让用户选）
 *   已废弃 localStorage ZCJF-STORAGE（vuex-persistedstate 插件），职责被 Redis 完全覆盖
 *   且自动静默恢复是 bug 制造机（多次 skipRestore 没清干净的根因）
 *
 * 防重复弹窗机制（银行保函 step2→step1 上一步场景）：
 *   step2 点上一步跳回 step1 时 URL 带 step2Id=step2，initWithDraftCheck 里守卫
 */
import { queryGuaranteeDraft, saveGuaranteeDraft, clearGuaranteeDraft } from '@/api/comm'
import { cloneDeep } from 'lodash'

export const ORDER_TYPE = {
  BANK_8000: '8000',
  ELECTRONIC_9600: '9600',
  INSURANCE_7000: '7000',
  PERFORMANCE_9100: '9100',
}

/**
 * 获取当前路由参数中的 outerOrderNo
 */
function getOuterOrderNo(vm) {
  return (vm.$route && vm.$route.query && vm.$route.query.outerOrderNo) || ''
}

/**
 * 判断当前是否为"新订单场景"：
 *   - orderID 不存在 且 orderId 不存在
 */
function isNewOrderScene(vm) {
  const q = (vm.$route && vm.$route.query) || {}
  return !q.orderID && !q.orderId
}

/**
 * 页面进入时检查并处理草稿恢复弹窗。
 *
 * 调用时机：主页面 created() 中，仅当 isNewOrderScene 为 true 时调用。
 *
 * @param {Object} vm                  Vue 组件实例（用于调 $confirm、$route、VueRouter）
 * @param {string} orderType           保函类型（ORDER_TYPE 枚举）
 * @param {Object} handlers
 * @param {Function} handlers.restore     用户点击【继续申请】时触发：(draft) => void
 * @param {Function} handlers.skipRestore 用户点击【重新申请】时触发：() => void （可选）
 * @returns {Promise<boolean>} 是否已显示弹窗（调用方可据此决定是否走正常初始化流程）
 */
export async function checkGuaranteeDraft(vm, orderType, handlers) {
  if (!isNewOrderScene(vm)) {
    return false
  }

  const outerOrderNo = getOuterOrderNo(vm)

  try {
    const res = await queryGuaranteeDraft({ orderType, outerOrderNo })
    const draft = res && res.data
    if (!draft || !draft.formData) {
      return false
    }

    // 有草稿，弹窗
    vm.$confirm(
      '存在已保存的订单未完成，请选择继续申请未完成订单或重新申请新订单',
      '提示',
      {
        confirmButtonText: '继续申请',
        cancelButtonText: '重新申请',
        type: 'warning',
        closeOnClickModal: false,
        closeOnPressEscape: false,
        showClose: false,
      }
    )
      .then(async () => {
        // 主按钮：继续申请 —— 只恢复数据，不清 Redis（草稿要留到用户点提交/重新申请才消）
        try {
          await handlers.restore(draft)
        } catch (e) {
          console.error('继续申请处理异常', e)
        }
      })
      .catch(async () => {
        // 次要按钮：重新申请 —— 清 Redis + 走正常流程
        try {
          await clearGuaranteeDraft({ orderType, outerOrderNo })
        } catch (e) {
          console.warn('清空历史草稿失败', e)
        }
        if (handlers.skipRestore) handlers.skipRestore()
      })

    return true
  } catch (e) {
    console.warn('查询草稿接口异常，忽略草稿检查', e)
    return false
  }
}

/**
 * 保存当前步骤到 Redis 草稿
 *
 * @param {Object} vm          Vue 组件实例
 * @param {string} orderType   保函类型
 * @param {string|number} step 当前步骤标识（如 'step1'/'step2'、0/1/2、'1'/'2'）
 * @param {Object} formData    当前表单数据（会做深拷贝，避免后续修改影响缓存）
 */
export async function saveCurrentDraft(vm, orderType, step, formData) {
  const outerOrderNo = getOuterOrderNo(vm)
  try {
    await saveGuaranteeDraft({
      orderType,
      outerOrderNo,
      step,
      formData: cloneDeep(formData),
    })
    vm.$message.success('已保存')
    return true
  } catch (e) {
    console.error('保存草稿失败', e)
    vm.$message.error('保存草稿失败，请稍后重试')
    return false
  }
}

/**
 * 清空指定类型的 Redis 草稿（提交成功 / 重新申请后调用）
 */
export async function clearCurrentDraft(vm, orderType) {
  const outerOrderNo = getOuterOrderNo(vm)
  try {
    await clearGuaranteeDraft({ orderType, outerOrderNo })
    return true
  } catch (e) {
    console.warn('清空草稿失败', e)
    return false
  }
}
